import { uniqBy } from 'lodash';

import { isDocumentProjectType, isLongFormProjectType, type ProjectType } from 'src/data/project';
import { doesTemplateOutputIncludeImages, type TemplateOutputType } from 'src/data/template';

import { GENERATION_TOOL, type GenerationTool } from './const';
import {
  getToolDescription,
  getToolMixtureSubGenerationTools,
  getToolTitle,
  isMixtureTool,
} from './toolProps';
import { isBlankLongFormGenerationTool, isFreestyleGenerationTool } from './utils';

interface SectionToolOption {
  tool?: GenerationTool;
  subheader?: string;
}

const OPTIONS: SectionToolOption[] = [
  { subheader: 'Landing page' },
  { tool: GENERATION_TOOL.landingHeadline },
  { tool: GENERATION_TOOL.landingSubheader },
  { tool: GENERATION_TOOL.landingSectionHeadline },
  { tool: GENERATION_TOOL.landingCTA },
  { tool: GENERATION_TOOL.landingBenefits },
  { tool: GENERATION_TOOL.landingFeatures },
  { subheader: 'Website' },
  { tool: GENERATION_TOOL.landingSEOMetaTitle },
  { tool: GENERATION_TOOL.landingSEOMetadata },
  { subheader: 'Blog' },
  { tool: GENERATION_TOOL.articleBlogHeadline },
  { tool: GENERATION_TOOL.blogPostSectionSubHeadline },
  { tool: GENERATION_TOOL.blogPostSectionHeadline },
  { tool: GENERATION_TOOL.blogPostIntroParagraph },
  { subheader: 'Email' },
  { tool: GENERATION_TOOL.emailSubject },
  { tool: GENERATION_TOOL.emailHeadline },
  { tool: GENERATION_TOOL.emailSectionHeadline },
  { tool: GENERATION_TOOL.emailBody },
  { tool: GENERATION_TOOL.emailIntroParagraph },
  { tool: GENERATION_TOOL.emailCta },
  { tool: GENERATION_TOOL.emailMixture },
  { subheader: 'Social' },
  { tool: GENERATION_TOOL.facebookPost },
  { tool: GENERATION_TOOL.linkedinPost },
  { tool: GENERATION_TOOL.instagramCaption },
  { tool: GENERATION_TOOL.pinterestPostTitle },
  { tool: GENERATION_TOOL.pinterestPostDescription },
  { tool: GENERATION_TOOL.tweet },
  { tool: GENERATION_TOOL.youtubeTitle },
  { tool: GENERATION_TOOL.youtubeDescription },
  { subheader: 'Ads' },
  { tool: GENERATION_TOOL.facebookPrimaryTextProduct },
  { tool: GENERATION_TOOL.facebookHeadlineProduct },
  { tool: GENERATION_TOOL.linkedinHeadlineSingleImage },
  { tool: GENERATION_TOOL.linkedinIntroSingleImage },
  { tool: GENERATION_TOOL.twitterCopyProduct },
  { tool: GENERATION_TOOL.twitterTitleProduct },
  { tool: GENERATION_TOOL.pinterestTitleProduct },
  { tool: GENERATION_TOOL.pinterestDescriptionProduct },
  { tool: GENERATION_TOOL.googleHeadlineProduct },
  { tool: GENERATION_TOOL.googleMediumHeadlineProduct },
  { tool: GENERATION_TOOL.googleLongHeadlineProduct },
  { tool: GENERATION_TOOL.googleDescriptionProduct },
  { tool: GENERATION_TOOL.outbrainHeadlineProduct },
  { tool: GENERATION_TOOL.outbrainDescriptionProduct },
  { tool: GENERATION_TOOL.taboolaHeadlineProduct },
  { tool: GENERATION_TOOL.taboolaDescriptionProduct },
  { subheader: 'Ecommerce' },
  { tool: GENERATION_TOOL.amazonProductListing },
  { tool: GENERATION_TOOL.walmartProductListing },
  { tool: GENERATION_TOOL.basicProductReview },
  { subheader: 'Other' },
  { tool: GENERATION_TOOL.smsPromotional },
];

const SECTION_HEADLINE_TOOLS_TO_EXCLUDE: GenerationTool[] = [
  GENERATION_TOOL.blogPostSectionHeadline,
  GENERATION_TOOL.landingSectionHeadline,
  GENERATION_TOOL.emailSectionHeadline,
];

const TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE: GenerationTool[] = [
  GENERATION_TOOL.emailColdOutreachBody,
  GENERATION_TOOL.emailPromotionalBody,
  GENERATION_TOOL.emailCta,
  GENERATION_TOOL.emailHeadline,
  GENERATION_TOOL.landingCTA,
];

const TOOLS_INCOMPATIBLE_WITH_LONG_FORM: GenerationTool[] = [GENERATION_TOOL.emailBody];

const TOOLS_INCOMPATIBLE_WITH_INFINITY: GenerationTool[] = [GENERATION_TOOL.emailBody];

const TOOLS_TO_REMOVE_FOR_FREESTYLE: GenerationTool[] = [
  GENERATION_TOOL.facebookPrimaryTextProduct,
  GENERATION_TOOL.facebookHeadlineProduct,
  GENERATION_TOOL.linkedinHeadlineSingleImage,
  GENERATION_TOOL.linkedinIntroSingleImage,
  GENERATION_TOOL.twitterCopyProduct,
  GENERATION_TOOL.twitterTitleProduct,
  GENERATION_TOOL.pinterestTitleProduct,
  GENERATION_TOOL.pinterestDescriptionProduct,
  GENERATION_TOOL.landingHeadline,
  GENERATION_TOOL.landingSubheader,
  GENERATION_TOOL.landingCTA,
  GENERATION_TOOL.landingBenefits,
  GENERATION_TOOL.landingFeatures,
  GENERATION_TOOL.landingSEOMetadata,
  GENERATION_TOOL.landingSEOMetaTitle,
  GENERATION_TOOL.landingSectionHeadline,
  GENERATION_TOOL.googleHeadlineProduct,
  GENERATION_TOOL.googleMediumHeadlineProduct,
  GENERATION_TOOL.googleDescriptionProduct,
];

const TOOLS_TO_ADD_FOR_FREESTYLE: GenerationTool[] = [
  GENERATION_TOOL.emailBody,
  GENERATION_TOOL.facebookMixtureAd,
  GENERATION_TOOL.twitterMixtureAd,
  GENERATION_TOOL.pinterestMixtureAd,
  GENERATION_TOOL.linkedinMixtureAd,
  GENERATION_TOOL.googleAppAd,
  GENERATION_TOOL.googleDemandGenAd,
  GENERATION_TOOL.googleDisplayAd,
  GENERATION_TOOL.googleRSA,
];

interface GetSectionToolsOptionsParams {
  projectType?: ProjectType;
  generationTool?: GenerationTool;
  templateOutputType?: TemplateOutputType;
  targetType?: 'infinity' | 'format_popup';
  withFreeStyle?: boolean;
  withoutSubHeaders?: boolean;
  removeSectionHeadlines?: boolean;
}

/**
 * Retrieves section tool options based on filtering criteria.
 * @param options - Options for filtering.
 * @param [options.projectType] - project type
 * @param [options.generationTool] - The main generation tool.
 * @param [options.withFreeStyle=false] - Whether to include freestyle writing.
 * @param [options.withoutSubHeaders=false] - Whether to exclude subheaders.
 * @param [options.removeSectionHeadlines=false] - Whether to exclude section headlines.
 * @returns - Array of filtered generation tool options.
 */
export const getSectionToolsOptions = ({
  projectType,
  generationTool,
  templateOutputType,
  targetType,
  withFreeStyle,
  withoutSubHeaders,
  removeSectionHeadlines,
}: GetSectionToolsOptionsParams): SectionToolOption[] => {
  let filteredOptions = OPTIONS;

  if (removeSectionHeadlines) {
    filteredOptions = filteredOptions.filter((option) =>
      option.tool ? !SECTION_HEADLINE_TOOLS_TO_EXCLUDE.includes(option.tool) : true
    );
  }
  if (withFreeStyle) {
    filteredOptions = [
      { subheader: 'General writing' },
      { tool: GENERATION_TOOL.freestyle },
      ...filteredOptions,
    ];
  }

  // filter tools based on project type
  if (projectType) {
    // filter tools for "long-form" projects
    if (isLongFormProjectType(projectType)) {
      filteredOptions = filteredOptions.filter((option) =>
        option.tool ? !TOOLS_INCOMPATIBLE_WITH_LONG_FORM.includes(option.tool) : true
      );

      filteredOptions = filteredOptions.reduce((result: SectionToolOption[], option) => {
        if (option.tool && isMixtureTool(option.tool)) {
          let subTools = getToolSubToolOptions(option.tool);

          // Filter out incompatible subTools if the generation tool is blank
          if (generationTool && isBlankLongFormGenerationTool(generationTool)) {
            subTools = subTools.filter(
              (subTool) =>
                subTool.tool && !TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE.includes(subTool.tool)
            );
          }

          return [...result, ...subTools];
        }

        return [...result, option];
      }, []);
    }

    // filter tools for "short-form" projects
    if (isDocumentProjectType(projectType)) {
      filteredOptions = filteredOptions.reduce((result: SectionToolOption[], option) => {
        if (option.tool && isMixtureTool(option.tool)) {
          const subTools = getToolSubToolOptions(option.tool);

          return [...result, ...subTools];
        }

        return [...result, option];
      }, []);
    }

    // Source: Template-Builder - If template should include image we need to include the image tool
    if (templateOutputType && doesTemplateOutputIncludeImages(templateOutputType)) {
      filteredOptions = [...filteredOptions, { tool: GENERATION_TOOL.imageByStyle }];
    }

    // ... filter tools for "format" popup or "infinity" extension
  } else {
    filteredOptions = filteredOptions.reduce((result: SectionToolOption[], option) => {
      if (option.tool && isMixtureTool(option.tool)) {
        let subTools = getToolSubToolOptions(option.tool);

        if (targetType === 'format_popup') {
          subTools = subTools.filter(
            (subTool) =>
              subTool.tool && !TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE.includes(subTool.tool)
          );
        }

        return [...result, ...subTools];
      }

      return [...result, option];
    }, []);

    if (targetType === 'infinity') {
      filteredOptions = filteredOptions.filter((option) =>
        option.tool ? !TOOLS_INCOMPATIBLE_WITH_INFINITY.includes(option.tool) : true
      );
      // replace "freestyle" with "basic content improver" for infinity, because it better works with "content improver" requests
      filteredOptions = filteredOptions.map((option) => {
        if (option.tool && isFreestyleGenerationTool(option.tool)) {
          return {
            ...option,
            tool: GENERATION_TOOL.basicContentImprover,
          };
        }
        return option;
      });
    }
  }

  if (withoutSubHeaders) {
    filteredOptions = filteredOptions.filter((option) => option.tool);
  } else {
    filteredOptions = removeEmptySubHeaders(filteredOptions);
  }

  return uniqBy(filteredOptions, ({ tool, subheader }) => tool || subheader);
};

/**
 * Prepare "tool list" object for backend
 * @param tools - tools
 * @returns - tool list object
 */
export const prepareToolListForBackend = (tools: GenerationTool[]): Record<string, string> => {
  return tools.reduce((toolList: Record<string, string>, tool) => {
    const title = getToolTitle(tool);
    const description = getToolDescription(tool);
    toolList[tool] = `${title}${description ? ` - ${description}` : ''}`;
    return toolList;
  }, {});
};

/**
 * Get available generation tools object for the backend.
 * @param options - Options for filtering generation tools.
 * @param [options.projectType] - Whether the project is a long-form project.
 * @param [options.generationTool] - A specific generation tool to filter by.
 * @returns - The prepared tool list object.
 */
export const getAvailableToolsForBackend = ({
  projectType,
  generationTool,
  templateOutputType,
}: {
  projectType: ProjectType;
  generationTool?: GenerationTool;
  templateOutputType?: TemplateOutputType;
}): Record<string, string> => {
  const filteredToolItems = getSectionToolsOptions({
    projectType,
    generationTool,
    templateOutputType,
    withoutSubHeaders: true,
    removeSectionHeadlines: true,
  });

  // take only "tool" values
  let tools = filteredToolItems
    .map((toolItem) => toolItem.tool)
    .filter(Boolean) as GenerationTool[];

  if (isDocumentProjectType(projectType)) {
    tools.push(GENERATION_TOOL.googlePerformanceMaxAd);
  }

  if (generationTool && isFreestyleGenerationTool(generationTool)) {
    tools = tools.filter((tool) => !TOOLS_TO_REMOVE_FOR_FREESTYLE.includes(tool));

    tools.push(...TOOLS_TO_ADD_FOR_FREESTYLE);
  }

  return prepareToolListForBackend(tools);
};

/**
 * Retrieves sub-tool options for a given tool.
 * @param tool - The tool for which to get sub-tools.
 * @returns - Array of sub-tool options.
 */
const getToolSubToolOptions = (tool: GenerationTool): SectionToolOption[] => {
  /** Need to remove the below from selectors:
   * 1. Email - Cold email (defined in emailMixture -> classifiedMixtureSubGenerationTools)
   * 2. Email - Promotional email (defined in emailMixture -> classifiedMixtureSubGenerationTools)
   * @see https://www.notion.so/anyword/SFCI-single-asset-content-types-minor-changes-3019e8d84afb800cb865d042791f3743
   */
  if (tool === GENERATION_TOOL.emailMixture) {
    return [];
  }

  const subTools = getToolMixtureSubGenerationTools(tool);

  return subTools.map((tool) => ({
    tool,
  }));
};

const removeEmptySubHeaders = (options: SectionToolOption[]): SectionToolOption[] => {
  return options.filter((option, index, items) => {
    const nextOption = items[index + 1];

    // Remove current option if it is a subheader and the next option is also a subheader
    return !(option.subheader && nextOption?.subheader);
  });
};
